/**
 * Date utility functions
 */

export interface DateRange {
  from: string // YYYY-MM-DD format
  to: string   // YYYY-MM-DD format
}

/**
 * Get today's date as YYYY-MM-DD string
 */
export function getTodayString(): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  // Use local timezone to avoid date shifting
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Get current date and time as datetime-local format string (YYYY-MM-DDTHH:mm)
 */
export function getTodayDateTimeLocal(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Get date range for a given period
 * @param period - 'day', 'week', or 'month'
 *   - 'day': base date to base date
 *   - 'week': 7 days window ending at base date
 *   - 'month': 30 days window ending at base date
 * @param weekStart - Deprecated: no longer used (kept for backward compatibility)
 * @param baseDate - Optional base date to anchor the period to (Date or ISO/YYYY-MM-DD string).
 *   If not provided, uses today.
 * @returns DateRange with from and to dates in YYYY-MM-DD format
 */
export function getDateRangeForPeriod(period: string, weekStart: string = 'sunday', baseDate?: Date | string): DateRange {
  const today = (() => {
    let d: Date
    if (baseDate) {
      d = typeof baseDate === 'string' ? new Date(baseDate) : new Date(baseDate)
    } else {
      d = new Date()
    }
    d.setHours(0, 0, 0, 0)
    return d
  })()

  // Helper function to format date as YYYY-MM-DD using local timezone
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  if (period === 'day') {
    const todayStr = formatLocalDate(today)
    return {
      from: todayStr,
      to: todayStr
    }
  } else if (period === 'week') {
    const weekStartDate = new Date(today)
    weekStartDate.setDate(today.getDate() - 7) // 6 days before today
    const weekEndDate = new Date(today)
    weekEndDate.setDate(today.getDate()) // Tomorrow
    
    return {
      from: formatLocalDate(weekStartDate),
      to: formatLocalDate(weekEndDate)
    }
  } else if (period === 'month') {
    const monthStartDate = new Date(today)
    monthStartDate.setDate(today.getDate() - 30) // 29 days before today
    const monthEndDate = new Date(today)
    monthEndDate.setDate(today.getDate()) // Tomorrow
    
    return {
      from: formatLocalDate(monthStartDate),
      to: formatLocalDate(monthEndDate)
    }
  }
  
  // Default to today if period is not recognized
  const todayStr = formatLocalDate(today)
  return {
    from: todayStr,
    to: todayStr
  }
}

/**
 * Format date string to YYYY-MM-DD format
 * @param date - Date object or ISO string
 * @returns YYYY-MM-DD string
 */
export function formatDateToYYYYMMDD(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toISOString().split('T')[0]
}

/**
 * Convert date string to ISO string (for API)
 * @param dateString - YYYY-MM-DD or YYYY-MM-DDTHH:mm format string
 * @returns ISO string
 */
export function dateStringToISO(dateString: string | null): string | null {
  if (!dateString) return null
  // If it's already in datetime-local format (YYYY-MM-DDTHH:mm), convert it properly
  if (dateString.includes('T')) {
    // datetime-local format: convert to ISO preserving local time
    const date = new Date(dateString)
    return date.toISOString()
  }
  // Date only format: convert to ISO (will use midnight UTC)
  return new Date(dateString).toISOString()
}

/**
 * Format ISO date string to datetime-local format (YYYY-MM-DDTHH:mm)
 * @param isoString - ISO date string
 * @returns datetime-local format string (YYYY-MM-DDTHH:mm)
 */
export function formatISOToDateTimeLocal(isoString: string | null): string | null {
  if (!isoString) return null
  const date = new Date(isoString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Get start of day (00:00:00) for a date
 * @param date - Date object or string
 * @returns Date object at start of day
 */
export function getStartOfDay(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const result = new Date(dateObj)
  result.setHours(0, 0, 0, 0)
  return result
}

/**
 * Get end of day (23:59:59.999) for a date
 * @param date - Date object or string
 * @returns Date object at end of day
 */
export function getEndOfDay(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const result = new Date(dateObj)
  result.setHours(23, 59, 59, 999)
  return result
}

/**
 * Format date for display (locale date string)
 * @param date - Date object or ISO string
 * @returns Formatted date string
 */
export function formatDateForDisplay(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString()
}

/**
 * Format time for display (locale time string)
 * @param date - Date object or ISO string
 * @returns Formatted time string
 */
export function formatTimeForDisplay(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleTimeString()
}
